import { KeyboardEvent } from 'react';
import styles from './MultiSelectOption.module.scss';
import { SubOption, Option } from '../MultiSelect';
import ArrowRight from 'ui/icons/icon-chevron-right.svg';
import ArrowDown from 'ui/icons/icon-chevron-down.svg';

interface MultiSelectOptionProps {
	option: Option;
	isParentSelected: boolean;
	isIndeterminate: boolean;
	expandedParents: string[];
	toggleParentExpansion: (value: string) => void;
	handleOptionClick: (option: Option) => void;
	handleSubOptionClick: (sub: SubOption) => void;
	handleKeyDown: (e: KeyboardEvent<HTMLElement>, handler: () => void) => void;
	value: string[];
}

export const MultiSelectOption = ({
	option,
	isParentSelected,
	isIndeterminate,
	expandedParents,
	toggleParentExpansion,
	handleOptionClick,
	handleSubOptionClick,
	handleKeyDown,
	value,
}: MultiSelectOptionProps) => {
	const expanded = expandedParents.includes(option.value);
	return (
		<li key={option.value} className={styles.MultiSelectOption}>
			<div className={styles.MultiSelectOption_itemHeader}>
				<div
					className={styles.MultiSelectOption_option}
					role="option"
					aria-selected={isParentSelected}
					aria-expanded={option.subOptions && option.subOptions.length > 0 && expandedParents.includes(option.value) && isParentSelected}
					tabIndex={0}
					onKeyDown={(e) => handleKeyDown(e, () => handleOptionClick(option))}
					onClick={() => handleOptionClick(option)}
				>
					<input
						tabIndex={-1}
						type="checkbox"
						checked={isParentSelected}
						ref={(el) => {
							if (el) el.indeterminate = isIndeterminate;
						}}
						readOnly
						className={styles.MultiSelectOption_checkbox}
						aria-hidden="true"
					/>
					{option.label}
				</div>

				{option.subOptions && option.subOptions.length > 0 && (
					<button
						type="button"
						className={styles.MultiSelectOption_arrow}
						onClick={(e) => {
							e.stopPropagation();
							toggleParentExpansion(option.value);
						}}
						aria-label={expandedParents.includes(option.value) ? 'Collapse suboptions' : 'Expand suboptions'}
					>
						{expandedParents.includes(option.value) ? (
							<span className={styles.MultiSelectOption_arrowIcon}>
								<ArrowDown />
							</span>
						) : (
							<span className={styles.MultiSelectOption_arrowIcon}>
								<ArrowRight />
							</span>
						)}
					</button>
				)}
			</div>

			{option.subOptions && (
				<ul className={`${styles.MultiSelectOption_subList} ${expanded ? styles.MultiSelectOption_subList___expanded : ''}`}>
					{option.subOptions.map((sub) => {
						const isSubSelected = value.includes(sub.value);
						return (
							<li
								key={sub.value}
								role="option"
								aria-selected={isSubSelected}
								tabIndex={expanded ? 0 : -1}
								onKeyDown={(e) => handleKeyDown(e, () => handleSubOptionClick(sub))}
								onClick={() => handleSubOptionClick(sub)}
								className={styles.MultiSelectOption_subOption}
							>
								<input
									tabIndex={-1}
									type="checkbox"
									checked={isSubSelected}
									readOnly
									className={styles.MultiSelectOption_checkbox}
									aria-hidden="true"
								/>
								{sub.label}
							</li>
						);
					})}
				</ul>
			)}
		</li>
	);
};
